import { AiMessageSteps } from "__generated__/graphql"

import {
  asRecord,
  parseJsonIfPossible,
  ROLE_LABELS
} from "./aiStepsDrawer.utils"
import {
  CommerceRecord,
  readCommerceRecords
} from "./commerceRunTimeline.utils"
import {
  ACTION_LABELS,
  CONFIDENCE_LABELS,
  CONVERSATION_STATE_LABELS,
  DELIVERY_ACTION_LABELS,
  DELIVERY_LABELS,
  SURFACE_LABELS,
  TOOL_ERROR_LABELS,
  toolLabel
} from "./v2AgentLoop.labels"
import {
  clip,
  collectReferences,
  readArguments,
  readModelText,
  References,
  summarizeToolResult
} from "./v2AgentLoop.utils"

/**
 * A raw step read as one system talking to another: what Qpien sent the model, the shop or the
 * knowledge base, and what came back. Someone who never read the engine can follow a run this way;
 * the JSON stays underneath for whoever needs the exact payload.
 */

export type StepActor =
  | "host"
  | "model"
  | "shop"
  | "knowledge"
  | "skill"
  | "external"
  | "marketplace"
  | "customer"

export type ReadableField = { label: string; value: string }

export type ReadableEvidence = {
  title: string
  text: string
  score?: number
  tags?: string[]
}

export type TranscriptSpeaker = "customer" | "host" | "model" | "tool"

export type TranscriptItem = {
  speaker: TranscriptSpeaker
  label: string
  text: string
}

export type ReadableBlock =
  | {
      kind: "text"
      label: string
      text: string
      /** What the customer actually reads. */
      emphasis?: boolean
      collapsed?: boolean
    }
  | { kind: "fields"; rows: ReadableField[] }
  | { kind: "list"; label: string; items: string[]; inline?: boolean }
  | { kind: "records"; label: string; rows: CommerceRecord[]; total: number }
  | {
      kind: "evidence"
      label: string
      items: ReadableEvidence[]
      total: number
    }
  | { kind: "transcript"; label: string; items: TranscriptItem[] }
  | { kind: "notice"; tone: "error" | "expected" | "info"; text: string }

export type ReadableExchange = {
  from: StepActor
  to: StepActor
  headline: string
  blocks: ReadableBlock[]
}

/** What one step needs from the rest of the run: skill/step names, and chunk text by id. */
export type ReadableContext = {
  refs: References
  chunks: Map<string, ReadableEvidence>
}

type Payload = Record<string, unknown>

const asText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : ""

const asNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null

const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : []

const asRecords = (value: unknown): Payload[] =>
  asArray(value)
    .map(asRecord)
    .filter((item): item is Payload => !!item)

const asTextList = (value: unknown): string[] =>
  asArray(value).map(asText).filter(Boolean)

const payload = (value: unknown): Payload | null =>
  asRecord(parseJsonIfPossible(value))

const stepName = (step: AiMessageSteps): string => String(step.step ?? "")

const isFailed = (step: AiMessageSteps): boolean =>
  String(step.status ?? "") === "FAILED"

const fmtInt = (n: number): string => n.toLocaleString("tr-TR")

const yesNo = (value: unknown): string =>
  value === true ? "evet" : value === false ? "hayır" : ""

const labelled = (labels: Record<string, string>, code: string): string =>
  code ? (labels[code] ?? code) : ""

type FieldValue = string | number | null | undefined

function fields(rows: [string, FieldValue][]): ReadableBlock | null {
  const kept = rows
    .filter(
      ([, value]) => value !== "" && value !== null && value !== undefined
    )
    .map(([label, value]) => ({ label, value: String(value) }))
  return kept.length ? { kind: "fields", rows: kept } : null
}

function compact(
  blocks: (ReadableBlock | null | undefined | false | "" | 0)[]
): ReadableBlock[] {
  return blocks.filter((block): block is ReadableBlock => !!block)
}

function list(
  label: string,
  items: string[],
  inline = false
): ReadableBlock | null {
  return items.length ? { kind: "list", label, items, inline } : null
}

function text(
  label: string,
  value: string,
  options: { emphasis?: boolean; collapsed?: boolean } = {}
): ReadableBlock | null {
  return value ? { kind: "text", label, text: value, ...options } : null
}

/** Catalogue descriptions arrive as HTML with escaped entities (`g&uuml;n`). */
export function htmlToText(value: string): string {
  if (!/[<&]/.test(value)) return value
  try {
    const doc = new DOMParser().parseFromString(value, "text/html")
    return (doc.body.textContent ?? value).replace(/\n{3,}/g, "\n\n").trim()
  } catch {
    return value
  }
}

function priceText(value: unknown): string {
  const price = asRecord(value)
  const amount = asText(price?.amount) || String(asNumber(price?.amount) ?? "")
  return amount
    ? [amount, asText(price?.currency)].filter(Boolean).join(" ")
    : ""
}

export function toolActor(tool: string): StepActor {
  if (tool === "search_knowledge" || tool === "skill_knowledge") {
    return "knowledge"
  }
  if (
    tool.startsWith("action_") ||
    tool === "skill_action" ||
    tool === "skill_write_action"
  ) {
    return "external"
  }
  if (tool.startsWith("skill_") || tool.endsWith("_skill")) return "skill"
  if (
    /^(resolve_|search_products|get_|compare_products|commerce_)/.test(tool)
  ) {
    return "shop"
  }
  return "host"
}

function describeArguments(raw: unknown, refs: References): string {
  const { intents, args } = readArguments(raw, refs)
  return [
    ...intents.map((intent) => `${intent.label}: ${intent.text}`),
    args.join(" · ")
  ]
    .filter(Boolean)
    .join("\n")
}

function chunkEvidence(
  meta: Payload | null,
  fallbackTitle: string,
  fallbackText: string,
  score: number | null,
  tags?: string[]
): ReadableEvidence {
  const title = [asText(meta?.topic), asText(meta?.subTopic)]
    .filter(Boolean)
    .join(" › ")
  return {
    title: title || asText(meta?.title) || fallbackTitle,
    text: asText(meta?.rawContent) || asText(meta?.content) || fallbackText,
    ...(score !== null ? { score } : {}),
    ...(tags?.length ? { tags } : {})
  }
}

const RAG_SOURCE_LABELS: Record<string, string> = {
  semantic: "anlam",
  bm25: "anahtar kelime"
}

function pineconeMatches(res: Payload | null): [string, ReadableEvidence][] {
  return asRecords(res?.matches).map((match) => {
    const id = asText(match.id)
    return [
      id,
      chunkEvidence(asRecord(match.metadata), id, "", asNumber(match.score))
    ]
  })
}

function bm25Hits(res: Payload | null): [string, ReadableEvidence][] {
  return asRecords(asRecord(res?.hits)?.hits).map((hit) => {
    const id = asText(hit._id)
    return [
      id,
      chunkEvidence(asRecord(hit._source), id, "", asNumber(hit._score))
    ]
  })
}

function fusedResults(res: Payload | null): [string, ReadableEvidence][] {
  return asRecords(res?.results).map((row) => {
    const id = asText(row.id)
    const tags = asTextList(row.sources).map(
      (source) => RAG_SOURCE_LABELS[source] ?? source
    )
    return [
      id,
      chunkEvidence(
        asRecord(row.metadata),
        id,
        asText(row.content),
        asNumber(row.score),
        tags
      )
    ]
  })
}

export function buildReadableContext(
  steps: AiMessageSteps[] | undefined | null
): ReadableContext {
  const list = steps ?? []
  const chunks = new Map<string, ReadableEvidence>()
  const remember = (entries: [string, ReadableEvidence][]) =>
    entries.forEach(([id, evidence]) => {
      if (id && !chunks.has(id)) chunks.set(id, evidence)
    })

  list.forEach((step) => {
    const res = payload(step.response)
    const name = stepName(step)
    if (name === "GET_PINECONE_DATA") remember(pineconeMatches(res))
    if (name === "GET_ELASTIC_BM25_DATA") remember(bm25Hits(res))
    if (name === "FUSE_RAG_RESULTS") remember(fusedResults(res))
  })

  return { refs: collectReferences(list), chunks }
}

function evidenceBlock(
  label: string,
  entries: ReadableEvidence[]
): ReadableBlock | null {
  return entries.length
    ? { kind: "evidence", label, items: entries, total: entries.length }
    : null
}

function recordsBlock(
  label: string,
  rows: CommerceRecord[]
): ReadableBlock | null {
  return rows.length
    ? { kind: "records", label, rows, total: rows.length }
    : null
}

function productBlocks(res: Payload): ReadableBlock[] {
  const summary = asRecord(res.offerSummary)
  const min = priceText(summary?.minPrice)
  const max = priceText(summary?.maxPrice)
  const offers = asRecords(res.offers)
  const inStock = asNumber(summary?.inStockOffers)
  const total = asNumber(summary?.totalOffers)
  const attributes = asArray(res.attributes).length

  const axes = asRecords(res.variantAxes).map((axis) => {
    const here = asTextList(axis.inListing)
    const across = asTextList(axis.acrossListings)
    const widened =
      across.length > here.length ? ` · tüm ilanlarda ${across.join(", ")}` : ""
    return `${asText(axis.name)}: bu ilanda ${here.join(", ") || "—"}${widened}`
  })

  return compact([
    fields([
      ["Ürün", asText(res.title)],
      ["Kategori", asText(asRecord(res.category)?.name)],
      ["Marka", asText(asRecord(res.brand)?.name)],
      ["Fiyat", min && max && min !== max ? `${min} – ${max}` : min],
      [
        "Stok",
        total !== null ? `${inStock ?? 0} / ${total} teklif stokta` : ""
      ],
      ["Özellik", attributes ? `${attributes} özellik alanı` : ""]
    ]),
    recordsBlock(
      "Teklifler (varyantlar)",
      readCommerceRecords({ items: offers })
    ),
    list("Varyant seçenekleri", axes),
    text("Açıklama", htmlToText(asText(res.description)), { collapsed: true })
  ])
}

function toolFailure(
  step: AiMessageSteps,
  res: Payload | null
): ReadableBlock | null {
  const failed =
    isFailed(step) || res?.ok === false || asText(res?.status) === "error"
  if (!failed) return null
  const code = asText(res?.error) || asText(step.reason)
  const known = TOOL_ERROR_LABELS[code]
  if (known) {
    return {
      kind: "notice",
      tone: known.expected ? "expected" : "error",
      text: `${known.text} (${code})`
    }
  }
  return { kind: "notice", tone: "error", text: code || "Araç hata döndürdü" }
}

function toolResult(
  step: AiMessageSteps,
  tool: string,
  raw: unknown
): Pick<ReadableExchange, "headline" | "blocks"> {
  const res = payload(raw)
  const { result, lines } = summarizeToolResult(tool, raw, asText(step.reason))
  const fallback = list("Özet", lines)
  const failure = toolFailure(step, res)

  if (failure || !res) {
    return { headline: result, blocks: compact([failure, fallback]) }
  }

  if (tool === "search_knowledge") {
    const found = asRecords(res.results).map((item) =>
      chunkEvidence(
        item,
        asText(item.ref),
        asText(item.excerpt),
        asNumber(item.score),
        asText(item.ref) ? [asText(item.ref)] : undefined
      )
    )
    return {
      headline: result,
      blocks: compact([
        text("Aranan", asText(res.query)),
        evidenceBlock("Bulunan bilgi parçaları", found)
      ])
    }
  }

  if (tool === "skill_knowledge") {
    const found = asTextList(res.chunks).map((chunk, index) => ({
      title: `Parça ${index + 1}`,
      text: chunk
    }))
    return {
      headline: result,
      blocks: compact([evidenceBlock("Bulunan bilgi parçaları", found)])
    }
  }

  const records = readCommerceRecords(res)
  if (records.length) {
    return {
      headline: result,
      blocks: compact([recordsBlock("Dönen kayıtlar", records)])
    }
  }

  if (asText(res.title)) {
    return { headline: result, blocks: productBlocks(res) }
  }

  if (Array.isArray(res.items) && res.items.length === 0) {
    return {
      headline: "Hiç kayıt dönmedi",
      blocks: compact([
        list(
          "Mağazanın verdiği sebep",
          asTextList(asRecord(res.diagnostics)?.reasonCodes)
        )
      ])
    }
  }

  return { headline: result, blocks: compact([fallback]) }
}

function contentText(content: unknown): string {
  if (typeof content === "string") return content.trim()
  return asRecords(content)
    .map((part) =>
      asText(part.type) === "input_image" ? "[görsel]" : asText(part.text)
    )
    .filter(Boolean)
    .join("\n")
}

/** Earlier assistant turns are the structured JSON; the reader wants the sentence that went out. */
function assistantText(raw: string): string {
  const structured = payload(raw)
  if (!structured || !("action" in structured)) return raw
  const reply = asText(structured.reply) || asText(structured.dmReply)
  return reply || `(${labelled(ACTION_LABELS, asText(structured.action))})`
}

function transcriptItem(
  item: Payload,
  refs: References
): TranscriptItem | null {
  const type = asText(item.type)
  const name = asText(item.name)

  if (type === "message") {
    const role = asText(item.role)
    const body = contentText(item.content)
    if (!body) return null
    if (role === "user")
      return { speaker: "customer", label: "Müşteri", text: body }
    if (role === "assistant") {
      return {
        speaker: "model",
        label: "AI'ın önceki cevabı",
        text: assistantText(body)
      }
    }
    return { speaker: "host", label: "Qpien notu", text: body }
  }

  if (type === "function_call") {
    return {
      speaker: "model",
      label: `Araç çağrısı · ${toolLabel(name)}`,
      text: describeArguments(item.arguments, refs) || name
    }
  }

  if (type === "function_call_result") {
    const output = asRecord(item.output)
    const raw: unknown = output && "text" in output ? output.text : item.output
    const { result, lines } = summarizeToolResult(name, raw, "")
    return {
      speaker: "tool",
      label: `Araç sonucu · ${toolLabel(name)}`,
      text: [result, ...lines].join("\n")
    }
  }

  return null
}

function decisionBlocks(output: Payload): ReadableBlock[] {
  const role = asText(output.answeringRole)
  const products = asArray(output.productIds).length
  const citations = asArray(output.citations).length

  return compact([
    text("Müşteriye yazdığı cevap", asText(output.reply), { emphasis: true }),
    text("Özelden gönderilecek cevap", asText(output.dmReply), {
      emphasis: true
    }),
    fields([
      ["Karar", labelled(ACTION_LABELS, asText(output.action))],
      ["Güven", labelled(CONFIDENCE_LABELS, asText(output.confidence))],
      ["Dil", asText(output.language)],
      [
        "Cevaplayan rol",
        role === "UNDECIDED"
          ? "henüz belirlenmedi"
          : labelled(ROLE_LABELS, role)
      ],
      [
        "Konuşma",
        labelled(CONVERSATION_STATE_LABELS, asText(output.conversationState))
      ],
      ["Devir gerekçesi", asText(output.handoffReason)],
      ["Eksik bilgi", asText(output.knowledgeGap)],
      ["Önerdiği ürün", products ? `${products} ürün` : ""],
      ["Dayandığı kaynak", citations ? `${citations} kaynak` : ""]
    ]),
    text("Ajanın notu (müşteri görmez)", asText(output.notes))
  ])
}

function usageFields(usage: Payload | null): ReadableBlock | null {
  if (!usage) return null
  const input = asNumber(usage.inputToken ?? usage.inputTokens)
  const output = asNumber(usage.outputToken ?? usage.outputTokens)
  const cached = asNumber(usage.cachedToken)
  const reasoning = asNumber(
    asRecords(usage.outputTokensDetails)[0]?.reasoning_tokens
  )
  return fields([
    ["Girdi token", input !== null ? fmtInt(input) : ""],
    ["Önbellekten", cached ? fmtInt(cached) : ""],
    ["Çıktı token", output !== null ? fmtInt(output) : ""],
    ["Düşünme token", reasoning ? fmtInt(reasoning) : ""]
  ])
}

function stepError(
  step: AiMessageSteps,
  res: Payload | null
): ReadableBlock | null {
  const error =
    asText(res?.error) || (isFailed(step) ? asText(step.reason) : "")
  return error ? { kind: "notice", tone: "error", text: error } : null
}

const toolName = (value: unknown): string =>
  typeof value === "string" ? value : asText(asRecord(value)?.name)

function readModelCall(
  step: AiMessageSteps,
  ctx: ReadableContext
): ReadableExchange[] {
  const req = payload(step.request)
  const res = payload(step.response)
  const model = asText(req?.model)
  const system = asText(req?.systemPrompt)
  const settings = asRecord(req?.settings)
  const maxTokens = asNumber(settings?.maxTokens)
  const outputType = asText(req?.outputType)
  const tools = asArray(req?.tools).map(toolName).filter(Boolean)
  const transcript = asRecords(req?.input)
    .map((item) => transcriptItem(item, ctx.refs))
    .filter((item): item is TranscriptItem => !!item)

  const output = asRecords(res?.output)
  const calls = output.filter((item) => asText(item.type) === "function_call")
  const written = readModelText(res?.output)
  const structured = payload(written)
  const error = stepError(step, res)

  const headline = error
    ? "Model hata döndürdü"
    : calls.length
      ? `${calls.length} araç çağırmaya karar verdi`
      : structured && "action" in structured
        ? `Cevabı yazdı · ${labelled(ACTION_LABELS, asText(structured.action))}`
        : written
          ? "Metin üretti"
          : "Boş döndü"

  return [
    {
      from: "host",
      to: "model",
      headline: `${transcript.length} konuşma öğesi ve ${tools.length} araçla modele soruldu`,
      blocks: compact([
        fields([
          ["Model", model],
          ["Düşünme seviyesi", asText(asRecord(settings?.reasoning)?.effort)],
          [
            "Maks. çıktı",
            maxTokens !== null ? `${fmtInt(maxTokens)} token` : ""
          ],
          [
            "Çıktı biçimi",
            outputType === "json_schema" ? "yapılandırılmış JSON" : outputType
          ],
          ["Sistem talimatı", system ? `${fmtInt(system.length)} karakter` : ""]
        ]),
        text("Sistem talimatı (prompt)", system, { collapsed: true }),
        transcript.length > 0 && {
          kind: "transcript",
          label: "Modelin gördüğü konuşma, sırayla",
          items: transcript
        },
        list(
          "Kullanabileceği araçlar",
          tools.map((tool) => toolLabel(tool)),
          true
        )
      ])
    },
    {
      from: "model",
      to: "host",
      headline,
      blocks: compact([
        error,
        list(
          "Çağırdığı araçlar",
          calls.map((call) => {
            const name = asText(call.name)
            const args = describeArguments(call.arguments, ctx.refs)
            return `${toolLabel(name)} (${name})${args ? `\n${args}` : ""}`
          })
        ),
        ...(structured && "action" in structured
          ? decisionBlocks(structured)
          : [text("Ürettiği metin", clip(written, 4000))]),
        usageFields(asRecord(res?.usage))
      ])
    }
  ]
}

function readAgentToolCall(
  step: AiMessageSteps,
  ctx: ReadableContext
): ReadableExchange[] {
  const req = payload(step.request)
  const tool = asText(req?.tool) || "tool"
  const target = toolActor(tool)
  const { intents, args } = readArguments(req?.arguments, ctx.refs)

  return [
    {
      from: "model",
      to: target,
      headline: `${toolLabel(tool)} · ${tool}`,
      blocks: compact([
        ...intents.map((intent) => text(intent.label, intent.text)),
        list("Parametreler", args)
      ])
    },
    { from: target, to: "model", ...toolResult(step, tool, step.response) }
  ]
}

const MCP_REQUEST_KEYS = new Set(["tool", "name", "prefetch"])

function readMcpToolCall(
  step: AiMessageSteps,
  ctx: ReadableContext
): ReadableExchange[] {
  const req = payload(step.request)
  const tool = asText(req?.tool) || asText(req?.name) || "tool"
  const args =
    asRecord(req?.args) ??
    Object.fromEntries(
      Object.entries(req ?? {}).filter(([key]) => !MCP_REQUEST_KEYS.has(key))
    )

  return [
    {
      from: "host",
      to: "shop",
      headline: `${toolLabel(tool)} · ${tool}`,
      blocks: compact([
        req?.prefetch === true && {
          kind: "notice",
          tone: "info",
          text: "Ajan başlamadan önce Qpien tarafından çekildi; sonuç ajana hazır bilgi olarak verildi."
        },
        list("Parametreler", readArguments(args, ctx.refs).args)
      ])
    },
    { from: "shop", to: "host", ...toolResult(step, tool, step.response) }
  ]
}

function readListTools(step: AiMessageSteps): ReadableExchange[] {
  const req = payload(step.request)
  const res = payload(step.response)
  const tools = asArray(res?.tools).map(toolName).filter(Boolean)
  const count = asNumber(res?.count) ?? tools.length

  return [
    {
      from: "host",
      to: "shop",
      headline: "Bu tur için kullanılabilecek araçlar istendi",
      blocks: compact([
        list("İzin verilen", asTextList(req?.allowed).map(toolLabel), true),
        list("Kapsam", asTextList(req?.scopes), true)
      ])
    },
    {
      from: "shop",
      to: "host",
      headline: `${count} araç kullanıma hazır`,
      blocks: compact([
        stepError(step, res),
        list("Araçlar", tools.map(toolLabel), true)
      ])
    }
  ]
}

function bm25Query(req: Payload | null): { query: string; filters: string[] } {
  const must = asRecords(asRecord(asRecord(req?.query)?.bool)?.must)
  let query = ""
  const filters: string[] = []
  must.forEach((clause) => {
    const match = asRecord(clause.multi_match)
    if (match) query = asText(match.query)
    const term = asRecord(clause.term)
    if (term) {
      Object.entries(term)
        .filter(([key]) => key !== "customerId")
        .forEach(([key, value]) => filters.push(`${key} = ${String(value)}`))
    }
  })
  return { query, filters }
}

function readRagStep(
  step: AiMessageSteps,
  ctx: ReadableContext
): ReadableExchange[] | null {
  const name = stepName(step)
  const req = payload(step.request)
  const res = payload(step.response)
  const evidence = (entries: [string, ReadableEvidence][]) =>
    entries.map(([, item]) => item)

  if (name === "GET_PINECONE_DATA") {
    const found = evidence(pineconeMatches(res))
    const topK = asNumber(req?.topK)
    return [
      {
        from: "host",
        to: "knowledge",
        headline: `Anlam araması${topK ? ` · en yakın ${topK} parça istendi` : ""}`,
        blocks: compact([
          req?.filter !== undefined &&
            text("Filtre", clip(JSON.stringify(req.filter), 300))
        ])
      },
      {
        from: "knowledge",
        to: "host",
        headline: found.length ? `${found.length} parça döndü` : "Sonuç yok",
        blocks: compact([
          stepError(step, res),
          evidenceBlock("Parçalar", found)
        ])
      }
    ]
  }

  if (name === "GET_ELASTIC_BM25_DATA") {
    const { query, filters } = bm25Query(req)
    const found = evidence(bm25Hits(res))
    const total = asNumber(asRecord(asRecord(res?.hits)?.total)?.value)
    return [
      {
        from: "host",
        to: "knowledge",
        headline: "Anahtar kelime araması",
        blocks: compact([text("Aranan", query), list("Filtreler", filters)])
      },
      {
        from: "knowledge",
        to: "host",
        headline:
          (total ?? found.length)
            ? `${total ?? found.length} eşleşme`
            : "Eşleşme yok",
        blocks: compact([
          stepError(step, res),
          evidenceBlock("Parçalar", found)
        ])
      }
    ]
  }

  if (name === "FUSE_RAG_RESULTS") {
    const found = evidence(fusedResults(res))
    return [
      {
        from: "host",
        to: "host",
        headline: `Anlam (${asNumber(req?.semanticCount) ?? 0}) ve anahtar kelime (${
          asNumber(req?.bm25Count) ?? 0
        }) sonuçları tek listede birleştirildi`,
        blocks: compact([
          evidenceBlock(`Birleşik liste · ${found.length} parça`, found)
        ])
      }
    ]
  }

  if (name === "RERANK_CHUNKS") {
    const ranked = Object.values(res ?? {})
      .map(asRecord)
      .filter((row): row is Payload => !!row && !!asText(row.id))
      .map((row) => {
        const id = asText(row.id)
        const known = ctx.chunks.get(id)
        const score = asNumber(row.score)
        return {
          title: known?.title ?? id,
          text: known?.text ?? "",
          ...(score !== null ? { score } : {})
        }
      })
    const topN = asNumber(req?.topN)
    const candidates = asNumber(req?.candidateCount)
    return [
      {
        from: "host",
        to: "knowledge",
        headline: `${candidates ?? "?"} aday içinden en alakalı ${topN ?? "?"} parça seçildi`,
        blocks: compact([
          text("Soru", asText(req?.query)),
          stepError(step, res),
          evidenceBlock("Seçilen parçalar (alakaya göre)", ranked)
        ])
      }
    ]
  }

  return null
}

function readHttp(step: AiMessageSteps, to: StepActor): ReadableExchange[] {
  const req = payload(step.request)
  const res = payload(step.response)
  const status = asNumber(res?.statusCode ?? res?.status)
  const failed =
    res?.requestFailed === true || res?.isError === true || isFailed(step)
  const body = res?.body ?? res?.data
  const bodyText =
    typeof body === "string" ? body : body ? JSON.stringify(body, null, 2) : ""

  return [
    {
      from: "host",
      to,
      headline: [asText(req?.method), asText(req?.url)]
        .filter(Boolean)
        .join(" "),
      blocks: []
    },
    {
      from: to,
      to: "host",
      headline:
        status !== null
          ? `HTTP ${status}${asText(res?.statusText) ? ` ${asText(res?.statusText)}` : ""}`
          : failed
            ? "İstek başarısız"
            : "Cevap döndü",
      blocks: compact([
        failed && {
          kind: "notice",
          tone: "error",
          text:
            asText(res?.message) ||
            asText(step.reason) ||
            "İstek başarısız oldu"
        },
        text("Gövde", clip(bodyText, 4000), {
          collapsed: bodyText.length > 400
        })
      ])
    }
  ]
}

function readShopProductDetails(step: AiMessageSteps): ReadableExchange[] {
  const req = payload(step.request)
  const res = payload(step.response)
  return [
    {
      from: "host",
      to: "shop",
      headline: "Ürün bilgisi Qpien Shop kataloğundan istendi",
      blocks: compact([
        fields([
          ["Ürün id", asText(req?.productId)],
          ["Kaynak", asText(req?.source)],
          ["Pasifleri de getir", yesNo(req?.includeInactive)]
        ])
      ])
    },
    {
      from: "shop",
      to: "host",
      headline: asText(res?.title) || "Ürün bulunamadı",
      blocks: compact([
        stepError(step, res),
        ...(res ? productBlocks(res) : [])
      ])
    }
  ]
}

function readAgentContext(step: AiMessageSteps): ReadableExchange[] {
  const req = payload(step.request)
  const res = payload(step.response)
  const guidance = asRecord(req?.guidance)
  const guidanceRows = asRecords(guidance?.rows)
  const skills = asRecord(req?.skills)
  const commerce = asRecord(req?.commerce)
  const clock = asRecord(req?.clock)
  const tools = asArray(req?.tools).map(toolName).filter(Boolean)
  const messages = asArray(req?.messageIds).length
  const instructions = asNumber(res?.instructionsChars)
  const dropped = asNumber(guidance?.dropped)
  const localTime = asText(clock?.localDateTime)

  return [
    {
      from: "host",
      to: "model",
      headline: `Ajana ${messages} müşteri mesajı, ${
        asNumber(guidance?.total) ?? guidanceRows.length
      } rehberlik kuralı ve ${tools.length} araç hazırlandı`,
      blocks: compact([
        fields([
          ["Rol", labelled(ROLE_LABELS, asText(req?.role))],
          ["Yüzey", labelled(SURFACE_LABELS, asText(req?.surface))],
          ["Kanal", asText(req?.channelType)],
          ["Dil", asText(req?.preferredLanguage)],
          ["Model", asText(req?.model)],
          ["Prompt katmanı", asText(req?.promptLayer)],
          [
            "Bilgi tabanı",
            req?.knowledgeAvailable === true
              ? "açık"
              : req?.knowledgeAvailable === false
                ? "kapalı"
                : ""
          ],
          [
            "Yerel saat",
            localTime
              ? `${localTime}${clock?.isOpen === true ? " · mesai içi" : clock?.isOpen === false ? " · mesai dışı" : ""}`
              : ""
          ],
          ["Önceki sorular", asArray(req?.previousQuestions).length || ""],
          ["Oturum geçmişi", asNumber(req?.sessionItems) || ""],
          [
            "Sistem talimatı",
            instructions ? `${fmtInt(instructions)} karakter` : ""
          ],
          ["Konuşma öğesi", asArray(res?.input).length || ""]
        ]),
        list(
          `Rehberlik kuralları${dropped ? ` · ${dropped} tanesi sığmadığı için çıkarıldı` : ""}`,
          guidanceRows.map((row) =>
            [asText(row.title), asText(row.content)].filter(Boolean).join(" — ")
          )
        ),
        fields([
          [
            "Aktif skill",
            asText(asRecord(skills?.active)?.name) || asText(skills?.active)
          ]
        ]),
        list("Aday skill'ler", asTextList(skills?.candidates), true),
        commerce?.enabled === true &&
          fields([
            ["Mağaza hattı", asText(commerce.lane)],
            ["Pazaryeri kaynağı", asText(commerce.marketplaceSource)]
          ]),
        list("Kanal action'ları", asTextList(req?.channelActions), true),
        list("Ajanın araçları", tools.map(toolLabel), true)
      ])
    }
  ]
}

function readAgentOutput(step: AiMessageSteps): ReadableExchange[] {
  const req = payload(step.request)
  const res = payload(step.response)
  const output = asRecord(res?.output)
  const used = asNumber(req?.turnsUsed)
  const max = asNumber(req?.maxTurns)

  return [
    {
      from: "model",
      to: "host",
      headline: output
        ? `Nihai karar · ${labelled(ACTION_LABELS, asText(output.action))}`
        : "Ajan karar üretemedi",
      blocks: compact([
        stepError(step, res),
        ...(output ? decisionBlocks(output) : []),
        fields([
          [
            "Kullanılan tur",
            used !== null ? `${used}${max ? ` / ${max}` : ""}` : ""
          ]
        ])
      ])
    }
  ]
}

function readDeliveryGate(step: AiMessageSteps): ReadableExchange[] {
  const req = payload(step.request)
  const res = payload(step.response)
  const code = asText(res?.code)
  const cards = readCommerceRecords({ items: res?.productCardList })

  return [
    {
      from: "host",
      to: "customer",
      headline: labelled(DELIVERY_LABELS, code) || asText(step.summary),
      blocks: compact([
        stepError(step, res),
        fields([
          ["Ajanın kararı", labelled(ACTION_LABELS, asText(req?.action))],
          ["Güven", labelled(CONFIDENCE_LABELS, asText(req?.confidence))],
          ["Cevap gönderildi", yesNo(res?.sent)],
          ["Özelden (DM)", res?.dm === true ? "evet" : ""],
          ["İnsana devredildi", yesNo(res?.handoff)],
          ["Onay bekliyor", res?.approval === true ? "evet" : ""],
          ["Ürün kartı", asNumber(res?.productCards) || ""]
        ]),
        list(
          "Yapılanlar",
          asTextList(res?.actions).map((action) =>
            labelled(DELIVERY_ACTION_LABELS, action)
          )
        ),
        recordsBlock("Gönderilen ürün kartları", cards)
      ])
    }
  ]
}

const READERS: Record<
  string,
  (step: AiMessageSteps, ctx: ReadableContext) => ReadableExchange[] | null
> = {
  AGENT_CONTEXT: readAgentContext,
  AGENT_MODEL_CALL: readModelCall,
  AGENT_TOOL_CALL: readAgentToolCall,
  AGENT_OUTPUT: readAgentOutput,
  DELIVERY_GATE: readDeliveryGate,
  COMMERCE_MCP_TOOL_CALL: readMcpToolCall,
  COMMERCE_MCP_LIST_TOOLS: readListTools,
  GET_QPIEN_SHOP_PRODUCT_DETAILS: readShopProductDetails,
  GET_PINECONE_DATA: readRagStep,
  GET_ELASTIC_BM25_DATA: readRagStep,
  FUSE_RAG_RESULTS: readRagStep,
  RERANK_CHUNKS: readRagStep,
  HTTP_REQUEST: (step) => readHttp(step, "external"),
  MARKETPLACE_REQUEST: (step) => readHttp(step, "marketplace")
}

export function hasReadableView(step: AiMessageSteps): boolean {
  return stepName(step) in READERS
}

/** Null when the step has no reader or its payload is not the shape the reader knows. */
export function readStep(
  step: AiMessageSteps,
  ctx: ReadableContext
): ReadableExchange[] | null {
  const reader = READERS[stepName(step)]
  if (!reader) return null
  try {
    const exchanges = reader(step, ctx)
    return exchanges?.length ? exchanges : null
  } catch {
    return null
  }
}
